import { ApolloClient, gql, NormalizedCacheObject } from '@apollo/client';
import getQueryAlias from './getQueryAlias';

const buildQuery = (
  compoundMetric: Metrics.CompoundMetricWithMetricDefs,
  filterInput: FilterInput[],
  dateScope: DateRangeInput,
  alias: string,
  toMetricInput: (metric: Metrics.NormalMetric | MetricInput) => MetricInput,
  groupBy?: V5GroupBy,
  trendByCalendarInterval?: FleetOps.Interval,
  trendByFiscalInterval?: FixedPeriods.FiscalType,
  trendByFixedIntervalDays?: number,
  groupByDayOfWeek?: boolean,
  groupByHour?: boolean,
  groupByMonth?: boolean,
) => {
  return {
    query: gql`
      query aggregateCompoundMetric(
        $metrics: [MetricInput]!
        $filters: [FilterInput]!
        $dateScope: [DateRangeInput!]!
        $expressions: [Expression2]!
        $groupBy: GroupBy
        $trendByCalendarInterval: Interval
        $trendByFiscalInterval: FiscalInterval
        $trendByFixedIntervalDays: Int
        $groupByDayOfWeek: Boolean
        $groupByHour: Boolean
        $groupByMonth: Boolean
      ) {
        ${alias}(
          metrics: $metrics
          filters: $filters
          dateScope: $dateScope
          expressions: $expressions
          groupBy: $groupBy
          trendByCalendarInterval: $trendByCalendarInterval
          trendByFiscalInterval: $trendByFiscalInterval
          trendByFixedIntervalDays: $trendByFixedIntervalDays
          groupByDayOfWeek: $groupByDayOfWeek
          groupByHour: $groupByHour
          groupByMonth: $groupByMonth
        )
      }
    `,
    variables: {
      metrics: compoundMetric.metrics.map(toMetricInput),
      filters: filterInput,
      dateScope,
      expressions: [
        {
          expression: compoundMetric.expression,
          id: compoundMetric.id,
        },
      ],
      groupBy,
      trendByCalendarInterval,
      trendByFixedIntervalDays,
      trendByFiscalInterval,
      groupByDayOfWeek,
      groupByHour,
      groupByMonth,
    },
  };
};

export const aggregateCompoundMetric = async ({
  compoundMetric,
  filterInput,
  dateScope,
  toMetricInput,
  client,
  groupBy,
  trendByCalendarInterval,
  trendByFiscalInterval,
  trendByFixedIntervalDays,
  groupByDayOfWeek,
  groupByHour,
  groupByMonth,
}: {
  compoundMetric: Metrics.CompoundMetricWithMetricDefs;
  filterInput: FilterInput[];
  dateScope: DateRangeInput;
  client: ApolloClient<NormalizedCacheObject>;
  toMetricInput: (metric: Metrics.NormalMetric | MetricInput) => MetricInput;
  groupBy?: V5GroupBy;
  trendByCalendarInterval?: FleetOps.Interval;
  trendByFiscalInterval?: FixedPeriods.FiscalType;
  trendByFixedIntervalDays?: number;
  groupByDayOfWeek?: boolean;
  groupByHour?: boolean;
  groupByMonth?: boolean;
}) => {
  if (compoundMetric.metrics.length === 0) {
    throw new Error(
      `Cannot aggregate an empty metric list for compound metric: ${compoundMetric.id}`,
    );
  }
  const alias = getQueryAlias({
    groupBy,
    trendByCalendarInterval,
    trendByFixedIntervalDays,
    groupByDayOfWeek,
  });

  const query = buildQuery(
    compoundMetric,
    filterInput,
    dateScope,
    alias,
    toMetricInput,
    groupBy,
    trendByCalendarInterval,
    trendByFiscalInterval,
    trendByFixedIntervalDays,
    groupByDayOfWeek,
    groupByHour,
    groupByMonth,
  );

  const response = await client.query(query);
  return response.data.aggregateCompoundMetric;
};

export default aggregateCompoundMetric;
